import { useState, useEffect, useMemo, useCallback } from 'react';
import calendarService from '../services/calendarService';
import { useToast } from './useToast';

const useCalendar = () => {
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventi, setEventi] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventsCache, setEventsCache] = useState({});
  
  const { showSuccess, showError } = useToast();

  // Get cache key based on current view and date
  const getCacheKey = useCallback(() => {
    const dateStr = currentDate.toISOString().split('T')[0];
    if (currentView === 'year') {
      return `${currentView}-${currentDate.getFullYear()}`;
    }
    return `${currentView}-${dateStr}`;
  }, [currentView, currentDate]);

  // Calcola la settimana corrente
  const weekDates = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    startOfWeek.setDate(diff);
    
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return date;
    });
  }, [currentDate]);

  // Eventi di oggi
  const eventiOggi = useMemo(() => {
    const today = new Date().toDateString();
    return eventi.filter(evento => {
      const eventDate = new Date(evento.CAL_DataOraInizio);
      if (isNaN(eventDate.getTime())) {
        return false;
      }
      try {
        const weekBounds = calendarService.getWeekBounds(currentDate);
        return eventDate >= weekBounds.startDate && eventDate <= weekBounds.endDate;
      } catch (error) {
        return false;
      }
    })
  }, [currentDate, eventi]);
  
  // Load events based on current view and date
  const loadEvents = useCallback(async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const cacheKey = getCacheKey();
      
      // Check cache first
      if (eventsCache[cacheKey]) {
        setEventi(eventsCache[cacheKey]);
        setIsLoading(false);
        return;
      }
      
      let events = [];
      
      switch(currentView) {
        case 'today':
          events = await calendarService.getTodayEvents();
          break;
        case 'week':
          const allMonthEvents = await calendarService.getMonthEvents(
            currentDate.getFullYear(), 
            currentDate.getMonth() + 1
          );
          
          const weekBounds = calendarService.getWeekBounds(currentDate);
          events = allMonthEvents.filter(evento => {
            const eventDate = new Date(evento.CAL_DataOraInizio);
            return eventDate >= weekBounds.startDate && eventDate <= weekBounds.endDate;
          });
          break;
        case 'month':
          events = await calendarService.getMonthEvents(
            currentDate.getFullYear(), 
            currentDate.getMonth() + 1
          );
          break;
        case 'year':
          const yearData = await calendarService.getYearEvents(currentDate.getFullYear());
          
          if (yearData && yearData.eventiPerMese) {
            events = [];
            Object.values(yearData.eventiPerMese).forEach(monthEvents => {
              if (Array.isArray(monthEvents)) {
                events.push(...monthEvents);
              }
            });
          } else if (Array.isArray(yearData)) {
            events = yearData;
          } else {
            events = [];
          }
          break;
        default:
          events = await calendarService.getTodayEvents();
      }
      
      setEventi(events);
      
      // Cache the results
      setEventsCache(prev => ({
        ...prev,
        [cacheKey]: events
      }));
      
    } catch (error) {
      setError('Errore nel caricamento degli eventi');
      showError('Errore nel caricamento degli eventi');
      setEventi([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentView, currentDate, showError, getCacheKey, eventsCache, isLoading]);

  // Refresh events (force reload, no cache)
  const refreshEvents = useCallback(async (skipCache = true) => {
    try {
      setIsLoading(true);
      setError(null);
      
      let cleanEvents = [];
      
      switch(currentView) {
        case 'today':
          cleanEvents = await calendarService.getTodayEvents();
          break;
        case 'week':
          const allMonthEvents = await calendarService.getMonthEvents(
            currentDate.getFullYear(), 
            currentDate.getMonth() + 1
          );
          
          const weekBounds = calendarService.getWeekBounds(currentDate);
          cleanEvents = allMonthEvents.filter(evento => {
            const eventDate = new Date(evento.CAL_DataOraInizio);
            return eventDate >= weekBounds.startDate && eventDate <= weekBounds.endDate;
          });
          break;
        case 'month':
          cleanEvents = await calendarService.getMonthEvents(
            currentDate.getFullYear(), 
            currentDate.getMonth() + 1
          );
          break;
        case 'year':
          const yearData = await calendarService.getYearEvents(currentDate.getFullYear());
          
          if (yearData && yearData.eventiPerMese) {
            cleanEvents = [];
            Object.values(yearData.eventiPerMese).forEach(monthEvents => {
              if (Array.isArray(monthEvents)) {
                cleanEvents.push(...monthEvents);
              }
            });
          } else if (Array.isArray(yearData)) {
            cleanEvents = yearData;
          } else {
            cleanEvents = [];
          }
          break;
        default:
          cleanEvents = await calendarService.getTodayEvents();
      }
      
      setEventi(cleanEvents);
      
      // Update cache
      if (skipCache) {
        const cacheKey = getCacheKey();
        setEventsCache(prev => ({
          ...prev,
          [cacheKey]: cleanEvents
        }));
      }
      
    } catch (error) {
      setError('Errore nel caricamento degli eventi');
      showError('Errore nel caricamento degli eventi');
      setEventi([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentView, currentDate, showError, getCacheKey]);
  
  // Auto-load events when view or date changes
  useEffect(() => {
    loadEvents();
  }, [currentView, currentDate]);
  
  // Change view
  const changeView = useCallback((view) => {
    setCurrentView(view);
    
    // Clear cache and force reload for new view
    if (view !== currentView) {
      setEventsCache({});
    }
  }, [currentView]);
  
  // Navigate dates
  const navigateDate = useCallback((direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      
      switch(currentView) {
        case 'today':
          newDate.setDate(newDate.getDate() + direction);
          break;
        case 'week':
          newDate.setDate(newDate.getDate() + (direction * 7));
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() + direction);
          break;
        case 'year':
          newDate.setFullYear(newDate.getFullYear() + direction);
          break;
        default:
          newDate.setDate(newDate.getDate() + direction);
      }
      
      return newDate;
    });
  }, [currentView]);
  
  // Set specific date
  const setDate = useCallback((date) => {
    setCurrentDate(new Date(date));
  }, []);

  // Event operations
  const creaEvento = useCallback(async (datiEvento) => {
    try {
      setIsLoading(true);
      const nuovoEvento = await calendarService.createEvent(datiEvento);
      await refreshEvents();
      showSuccess('Evento creato con successo');
      return nuovoEvento;
    } catch (error) {
      showError('Errore nella creazione dell\'evento');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [refreshEvents, showSuccess, showError]);

  const modificaEvento = useCallback(async (eventId, datiEvento) => {
    try {
      setIsLoading(true);
      const eventoModificato = await calendarService.updateEvent(eventId, datiEvento);
      await refreshEvents();
      showSuccess('Evento aggiornato con successo');
      return eventoModificato;
    } catch (error) {
      showError('Errore nell\'aggiornamento dell\'evento');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [refreshEvents, showSuccess, showError]);

  const eliminaEvento = useCallback(async (eventId) => {
    try {
      setIsLoading(true);
      await calendarService.deleteEvent(eventId);
      await refreshEvents();
      showSuccess('Evento eliminato con successo');
    } catch (error) {
      showError('Errore nell\'eliminazione dell\'evento');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [refreshEvents, showSuccess, showError]);

  return {
    // State
    currentView,
    currentDate,
    eventi,
    weekDates,
    eventiOggi,
    isLoading,
    error,
    
    // Actions
    changeView,
    navigateDate,
    setDate,
    loadEvents,
    refreshEvents,
    creaEvento,
    modificaEvento,
    eliminaEvento,
    setIsLoading
  };
};

export default useCalendar;